import React from 'react';
import InfiniteScrollGallery from '../Login/components/InfiniteScrollGallery';
import { useRef, useState, useEffect } from 'react';
import { useApiCall } from "../../hooks/useApiCall"
import ErrorPopup from "../../components/ErrorPopup"
import LoadingSpinner from "../../components/LoadingSpinner"
import { authService } from "../../services/auth.service";


const ForgotPassword: React.FC = () => {
  const formRef = useRef<HTMLFormElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const { 
    isLoading, 
    error: apiError, 
    execute, 
    clearError 
  } = useApiCall({
    onSuccess: (result) => {
      const updateResult = result as { status: string };
      if (updateResult.status === "success") {
        setSuccess(true);
        formRef.current?.reset();
      }
    },
    onError: (err) => {
      console.error('Error sending reset email:', err);
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(errorMessage || 'An unexpected error occurred. Please try again.');
    }
  });

  const checkValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    await execute(async () => {
      const email = e.currentTarget.email.value;
      if (!email) {
        setError('Email is required');
        return;
      } 
      
      if (!checkValidEmail(email)) {
        setError('Please enter a valid email address');
        return;
      }

      const response = await authService.sendPasswordResetEmail(email);

      if (response.status === 'error') {
        throw new Error(response.message);
      }

      return response;
    });
  }

  const handleCloseError = () => {
    setError(null);
    clearError();
  };

  // Auto scroll to error when it occurs
  useEffect(() => {
    if (error) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [error]);


  return (
    <>
      {isLoading && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <LoadingSpinner size="lg" text="Sending reset link..." />
        </div>
      )}
      <div className="min-h-screen bg-white flex flex-col lg:flex-row w-full">
        <div className="hidden md:flex md:w-1/2 lg:w-2/3 bg-gray-100">
            <InfiniteScrollGallery />
        </div>
        <div className="w-full md:w-1/2 lg:w-1/3 flex justify-center items-center bg-white relative p-4 sm:p-6 lg:p-8">
          <div className="flex flex-col items-center gap-4 sm:gap-6 w-full max-w-sm sm:max-w-md">
            <div className="font-poppins text-lg sm:text-xl lg:text-2xl font-semibold text-[#1a1a1a] text-center">
                Reset your password
            </div>
            
            {/* Forgot Password Form */}
            <div className="w-full flex flex-col items-center justify-center">
              <form ref={formRef} onSubmit={handleSubmit} className="w-full max-w-sm sm:max-w-md space-y-4 sm:space-y-6">
                <div className="space-y-3 sm:space-y-4 w-full">

                  {apiError && (
                    <div className={`p-3 text-xs sm:text-sm rounded-lg ${
                      apiError.includes("Successfully") || apiError.includes("sent") 
                        ? "bg-green-50 border border-green-200 text-green-600"
                        : "bg-red-50 border border-red-200 text-red-500"
                    }`}>
                      {apiError}
                    </div>
                  )}
                  {success && (
                    <div className="p-3 text-xs sm:text-sm rounded-lg bg-green-50 border border-green-200 text-green-600">
                      An email has been sent to your email address. Please check your inbox.
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <label htmlFor="email" className="sr-only">
                        Email
                    </label>
                    <div className="relative">
                        <input
                            id="email"
                            type="email"
                            placeholder="yours@example.com"
                            required
                             className="w-full h-12 sm:h-12 rounded-lg border border-[#d9d9d9] bg-[#ffffff] pl-3 pr-3 py-2 sm:py-3 text-base sm:text-base text-[#0f1d2a] placeholder:text-[#0f1d2a]/50 focus:outline-none focus:ring-2 focus:ring-[#2e4895] transition-all duration-200"
                        />
                    </div>
                  </div>
                </div>

                <button 
                  type="submit" 
                  disabled={isLoading}
                  className="w-full bg-[#0f1d2a] py-3 sm:py-3.5 rounded-lg text-[#ffffff] transition-colors duration-200 text-base sm:text-base disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 h-12 sm:h-12 font-medium"
                >
                  {isLoading ? (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                      <LoadingSpinner size="lg" text="Sending reset link..." />
                    </div>
                  ) : (
                    'Send reset link'
                  )}
                </button>

                {/* Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-base sm:text-base">
                    <span className="px-2 bg-white text-gray-500">or</span>
                  </div>
                </div>
              </form>

              {/* Sign In Link */}
              <p className="text-center text-base sm:text-base text-gray-600 font-poppins mt-4 sm:mt-6">
                Remember your password?{' '}
                <a href="/login" className="text-gray-600 hover:text-blue-700 transition-colors underline font-medium">
                  Sign In
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
      <ErrorPopup
        isOpen={!!error}
        onClose={handleCloseError}
        title="Reset Password Error"
        message={error || ''}
        type="error"
        actions={[
          {
            label: 'OK',
            onClick: handleCloseError,
            variant: 'primary'
          }
        ]}
      />
    </>
  );
};

export default ForgotPassword;
